import { z } from 'zod';
import { IdSchema } from './encoding.js';
/**
 * Accounts Payable contracts (ERD §5.9; FRD-FIN-070 to 077).
 *
 * Money is a **decimal string** at the amount scale, never a JavaScript number (SDD §9.2).
 * Payment runs and three-way matching are Phase 2 and absent here.
 */
const AmountSchema = z
    .string()
    .regex(/^\d{1,17}(\.\d{1,2})?$/, 'must be a decimal amount with at most 2 decimals');
const BusinessDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
export const SupplierInvoiceLineSchema = z.object({
    id: IdSchema,
    lineNo: z.number().int(),
    description: z.string(),
    accountId: IdSchema,
    netAmount: z.string(),
    costCenterId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    clientId: IdSchema.nullable(),
    employeeId: IdSchema.nullable(),
    assetId: IdSchema.nullable(),
    locationId: IdSchema.nullable(),
    /** The Marketing Campaign the line belongs to (CLR-18); a reference, not a dimension. */
    campaignId: IdSchema.nullable(),
});
export const SupplierInvoiceSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    supplierId: IdSchema,
    supplierInvoiceNumber: z.string(),
    supplierInvoiceDate: z.string(),
    taxDate: z.string(),
    dueDate: z.string().nullable(),
    verificationStatus: z.string(),
    verifiedAt: z.string().nullable().optional(),
    verifiedBy: IdSchema.nullable().optional(),
    approvalStatus: z.string(),
    paymentStatus: z.string(),
    status: z.string(),
    netTotal: z.string(),
    vatTotal: z.string(),
    grossTotal: z.string(),
    outstandingAmount: z.string(),
    journalId: IdSchema.nullable(),
    description: z.string().nullable(),
    version: z.number().int(),
});
/** A related record as its owning module describes it (SDD §5.6), e.g. `SYN-JV-000001 (posted)`. */
export const RecordSummarySchema = z.object({ id: IdSchema, summary: z.string().nullable() });
export const SupplierInvoiceDetailLineSchema = SupplierInvoiceLineSchema.extend({
    accountCode: z.string().nullable(),
    accountName: z.string().nullable(),
    /** The named campaign as Marketing describes it (`CODE name`); null when the line names none. */
    campaignSummary: z.string().nullable(),
    /** The VAT retained at capture (FRD-VAT-024); never recalculated for display. */
    vat: z
        .object({
        treatment: z.string(),
        rateApplied: z.string(),
        vatAmount: z.string(),
        recoverableAmount: z.string(),
        nonRecoverableAmount: z.string(),
        /** The VAT record this one adjusts, and its adjustment reference (FRD-VAT-024, VAT-037). */
        adjustedDetailId: IdSchema.nullable(),
        adjustmentReference: z.string().nullable(),
    })
        .nullable(),
});
export const SupplierInvoiceDetailSchema = z.object({
    invoice: SupplierInvoiceSchema,
    supplier: RecordSummarySchema,
    journal: RecordSummarySchema.nullable(),
    lines: z.array(SupplierInvoiceDetailLineSchema),
});
/** A line names the account it posts to (FRD-FIN-071). No account is inferred from its text. */
const DocumentLineRequestSchema = z.strictObject({
    description: z.string().min(1).max(500),
    accountId: IdSchema,
    netAmount: AmountSchema,
    costCenterId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    clientId: IdSchema.optional(),
    employeeId: IdSchema.optional(),
    assetId: IdSchema.optional(),
    locationId: IdSchema.optional(),
});
export const CreateSupplierInvoiceRequestSchema = z.strictObject({
    supplierId: IdSchema,
    supplierInvoiceNumber: z.string().min(1).max(100),
    supplierInvoiceDate: BusinessDateSchema,
    taxDate: BusinessDateSchema,
    dueDate: BusinessDateSchema.optional(),
    description: z.string().max(500).optional(),
    organizationId: IdSchema,
    lines: z
        .array(
    // An invoice line may name the Marketing Campaign it belongs to (CLR-18): optional. It may
    // also override the determined tax code, with a reason, under that authority (FRD-VAT-019).
    DocumentLineRequestSchema.extend({
        campaignId: IdSchema.optional(),
        taxOverride: z
            .strictObject({ taxCodeId: IdSchema, reason: z.string().trim().min(5).max(500) })
            .optional(),
    }))
        .min(1)
        .max(500),
});
export const SupplierCreditNoteSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    supplierInvoiceId: IdSchema,
    supplierCreditNoteReference: z.string(),
    creditNoteDate: z.string(),
    taxDate: z.string(),
    reason: z.string(),
    status: z.string(),
    netTotal: z.string(),
    vatTotal: z.string(),
    grossTotal: z.string(),
    journalId: IdSchema.nullable(),
    version: z.number().int(),
});
/** A credit note as a list shows it: with the credited invoice's number and its supplier. */
export const SupplierCreditNoteListItemSchema = SupplierCreditNoteSchema.extend({
    supplierInvoiceDocumentNo: z.string(),
    supplierId: IdSchema,
});
/**
 * One credit note with what its detail view shows (SDD §8.9): the invoice it credits, that
 * invoice's supplier, the journal, and the lines with their accounts and retained VAT.
 */
export const SupplierCreditNoteDetailSchema = z.object({
    creditNote: SupplierCreditNoteSchema,
    invoice: z.object({
        id: IdSchema,
        documentNo: z.string(),
        supplierInvoiceNumber: z.string(),
        status: z.string(),
        outstandingAmount: z.string(),
    }),
    supplier: RecordSummarySchema,
    journal: RecordSummarySchema.nullable(),
    lines: z.array(SupplierInvoiceDetailLineSchema.extend({
        /** The invoice line this line credits, where it credits one. */
        creditedInvoiceLine: z.object({ id: IdSchema, lineNo: z.number().int() }).nullable(),
    })),
});
export const CreateSupplierCreditNoteRequestSchema = z.strictObject({
    supplierInvoiceId: IdSchema,
    supplierCreditNoteReference: z.string().min(1).max(100),
    creditNoteDate: BusinessDateSchema,
    taxDate: BusinessDateSchema,
    reason: z.string().min(1).max(500),
    organizationId: IdSchema,
    lines: z
        .array(DocumentLineRequestSchema.extend({
        /** The invoice line this line credits; its VAT record becomes the adjusted original. */
        supplierInvoiceLineId: IdSchema.optional(),
    }))
        .min(1)
        .max(500),
});
/** The outstanding payable for one supplier (FRD-FIN-077). */
export const SupplierPayableBalanceSchema = z.object({
    supplierId: IdSchema,
    invoiceCount: z.number().int(),
    outstandingAmount: z.string(),
});
/**
 * Bank & Cash and Supplier Payment (ERD §5.9, §5.11; FRD-FIN-076, FIN-091 to 095, FIN-097).
 *
 * **AED only** (D5, FRD-FIN-095): there is no currency field, because the model defines none.
 * There is no payment-method field for the same reason. Payment runs are Phase 2 (FRD-FIN-079).
 */
export const BankCashAccountKindSchema = z.enum(['bank', 'cash_on_hand', 'petty_cash']);
export const ReconciliationStatusSchema = z.enum(['unreconciled', 'reconciled']);
export const BankCashTransactionKindSchema = z.enum([
    'deposit',
    'withdrawal',
    'receipt',
    'payment',
]);
export const CreateBankCashAccountRequestSchema = z
    .object({
    code: z
        .string()
        .regex(/^[A-Z][A-Z0-9_-]{0,19}$/, 'must be an uppercase code of at most 20 characters'),
    name: z.string().min(1).max(200),
    accountKind: BankCashAccountKindSchema,
    bankName: z.string().min(1).max(200).optional(),
    bankAccountIdentifier: z.string().min(1).max(100).optional(),
    /** The COA GL account this register stands for (ERD §5.11). */
    glAccountId: IdSchema,
})
    .strict()
    .refine((value) => (value.accountKind === 'bank') ===
    (value.bankName !== undefined && value.bankAccountIdentifier !== undefined), 'a bank kind carries a bank name and account identifier; a cash kind carries neither');
export const UpdateBankCashAccountRequestSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    bankName: z.string().min(1).max(200).nullable().optional(),
    bankAccountIdentifier: z.string().min(1).max(100).nullable().optional(),
    reconciliationStatus: ReconciliationStatusSchema.optional(),
    status: z.enum(['active', 'inactive']).optional(),
})
    .strict();
export const BankCashAccountSchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    accountKind: z.string(),
    bankName: z.string().nullable(),
    bankAccountIdentifier: z.string().nullable(),
    glAccountId: IdSchema,
    reconciliationStatus: z.string(),
    status: z.string(),
    version: z.number().int(),
});
/** A direct deposit or withdrawal, which posts from the movement itself (MD-08). */
export const CreateBankCashMovementRequestSchema = z
    .object({
    bankCashAccountId: IdSchema,
    transactionDate: BusinessDateSchema,
    kind: z.enum(['deposit', 'withdrawal']),
    amount: AmountSchema,
    /** The other side of the posting. The COA validates it; no account is assumed here. */
    contraAccountId: IdSchema,
    reference: z.string().max(200).optional(),
    description: z.string().max(500).optional(),
    /**
     * The contra line's dimensions, as its account's COA flags require (FRD-FIN-020, FIN-092).
     */
    costCenterId: IdSchema.optional(),
    employeeId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    clientId: IdSchema.optional(),
    assetId: IdSchema.optional(),
    locationId: IdSchema.optional(),
})
    .strict();
/** The one change a movement takes after posting: its reconciliation status (FRD-FIN-097). */
export const UpdateBankCashMovementRequestSchema = z
    .object({ reconciliationStatus: ReconciliationStatusSchema })
    .strict();
export const BankCashTransactionSchema = z.object({
    id: IdSchema,
    bankCashAccountId: IdSchema,
    transactionDate: z.string(),
    kind: z.string(),
    amount: z.string(),
    reference: z.string().nullable(),
    description: z.string().nullable(),
    reconciliationStatus: z.string(),
    contraAccountId: IdSchema.nullable(),
    costCenterId: IdSchema.nullable(),
    employeeId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    clientId: IdSchema.nullable(),
    assetId: IdSchema.nullable(),
    locationId: IdSchema.nullable(),
    status: z.string(),
    journalId: IdSchema.nullable(),
    version: z.number().int(),
});
/** A register by code and name — never by its bank account identifier. */
export const RegisterSummarySchema = z.object({ id: IdSchema, code: z.string(), name: z.string() });
/** One movement with its register, contra account, journal and realising payment (MD-08). */
export const BankCashMovementDetailSchema = z.object({
    movement: BankCashTransactionSchema,
    register: RegisterSummarySchema.nullable(),
    contraAccount: z.object({ id: IdSchema, code: z.string(), name: z.string() }).nullable(),
    journal: RecordSummarySchema.nullable(),
    payment: z
        .object({
        id: IdSchema,
        documentNo: z.string(),
        status: z.string(),
        journal: RecordSummarySchema.nullable(),
    })
        .nullable(),
});
/**
 * The consolidated cash position (FRD-FIN-093): every register's balance from posted movements
 * and the total, AED only (D5). Current position only — nothing is forecast.
 */
export const CashPositionSchema = z.object({
    total: z.string(),
    registers: z.array(z.object({
        id: IdSchema,
        code: z.string(),
        name: z.string(),
        accountKind: z.string(),
        status: z.string(),
        balance: z.string(),
    })),
});
export const BankCashBalanceSchema = z.object({
    bankCashAccountId: IdSchema,
    balance: z.string(),
});
/** One allocation of a payment against a posted supplier invoice (FRD-FIN-076). */
export const PaymentAllocationInputSchema = z
    .object({
    supplierInvoiceId: IdSchema,
    allocatedAmount: AmountSchema,
    allocationDate: BusinessDateSchema,
})
    .strict();
export const CreateSupplierPaymentRequestSchema = z
    .object({
    supplierId: IdSchema,
    paymentDate: BusinessDateSchema,
    amount: AmountSchema,
    bankCashAccountId: IdSchema,
    reference: z.string().max(200).optional(),
    description: z.string().max(500).optional(),
    allocations: z.array(PaymentAllocationInputSchema).max(200),
})
    .strict();
export const PaymentAllocationSchema = z.object({
    id: IdSchema,
    paymentId: IdSchema,
    supplierInvoiceId: IdSchema.nullable(),
    /** The claim an employee payment settles (MD-09). */
    expenseClaimId: IdSchema.nullable().optional(),
    allocatedAmount: z.string(),
    allocationDate: z.string(),
});
export const PaymentSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    paymentDate: z.string(),
    amount: z.string(),
    payeeType: z.string(),
    supplierId: IdSchema.nullable(),
    employeeId: IdSchema.nullable().optional(),
    bankCashTransactionId: IdSchema,
    status: z.string(),
    journalId: IdSchema.nullable(),
    reference: z.string().nullable(),
    description: z.string().nullable(),
    allocatedAmount: z.string(),
    unallocatedAmount: z.string(),
    version: z.number().int(),
});
export const PaymentDetailSchema = z.object({
    payment: PaymentSchema,
    supplier: RecordSummarySchema.nullable(),
    /** The employee payee of a claim settlement (MD-09). */
    employee: RecordSummarySchema.nullable().optional(),
    register: RegisterSummarySchema.nullable(),
    journal: RecordSummarySchema.nullable(),
    allocations: z.array(PaymentAllocationSchema.extend({
        invoiceDocumentNo: z.string().nullable(),
        supplierInvoiceNumber: z.string().nullable(),
        claimDocumentNo: z.string().nullable().optional(),
    })),
});
/**
 * Campaign spend (CLR-18; FRD-MKT-036, RPT-031): posted supplier invoice lines naming a campaign,
 * net of VAT, less posted credits of those lines. `from` / `to` are posting dates, inclusive; with
 * neither, everything to date.
 */
export const CampaignSpendQuerySchema = z.strictObject({
    from: BusinessDateSchema.optional(),
    to: BusinessDateSchema.optional(),
    campaignId: IdSchema.optional(),
});
export const CampaignSpendSchema = z.object({ campaignId: IdSchema, amount: z.string() });
export const CampaignSpendListSchema = z.array(CampaignSpendSchema);
//# sourceMappingURL=finance.js.map